/**
 * useClientOrgLabels — resolve owner/client org display labels for cross-org
 * rows at shared 3PL locations, in one place.
 *
 * Extracted from OrdersPage (BLU-389 Phase A.2) so the Orders queue and the
 * production-issues surfaces share ONE map + gating rule instead of hand-rolling
 * the `listConnectedClients` → alias lookup on every surface.
 *
 * `locations.listConnectedClients` is admin-only. For non-admin callers (e.g.
 * Warehouse Coordinators) the query resolves to no data, so `labelFor` returns
 * undefined and the chip falls back to a generic "Client" label — no crash, no
 * error UI. The query only runs when the shared-locations flag is on, so
 * single-tenant behaviour is unchanged (never fires, never a cross-org row).
 */
import { useCallback, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface ClientOrgLabels {
  /** Caller's own organization id (undefined until the org context loads). */
  callerOrgId: string | undefined;
  /**
   * orgId → display label. Caller's org → "{name} (You)"; connected clients →
   * `client_alias ?? client_org.name`. Used by the OrdersPage client filter
   * dropdown (keeps the "(You)" self-entry).
   */
  orgIdToName: Map<string, string>;
  /** Record variant for leaf components that take a plain object prop. */
  orgIdToNameRecord: Record<string, string>;
  /** True when `orgId` is a genuine non-caller org (i.e. a cross-org row). */
  isCrossOrg: (orgId: string | null | undefined) => boolean;
  /**
   * Resolved client display name for a row's org, or undefined when unknown
   * (own org, or a client the admin-only query hasn't surfaced). Chip call
   * sites render the generic fallback when this is undefined.
   */
  labelFor: (orgId: string | null | undefined) => string | undefined;
}

/** Row shape of `locations.listConnectedClients` (one row PER JUNCTION). */
type ConnectedRow = {
  client_organization_id?: string;
  client_org?: { name?: string } | null;
  client_alias?: string | null;
};

export function useClientOrgLabels(): ClientOrgLabels {
  const { organization } = useOrganization();
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  // Admin-only endpoint; non-admins resolve to no data and fall through to the
  // generic label. Mirrors the original OrdersPage query (enabled + staleTime).
  const connectedClientsQuery = trpc.locations.listConnectedClients.useQuery(undefined, {
    enabled: sharedLocationsEnabled,
    staleTime: 5 * 60_000,
    // Non-admins get a 403 the global retry predicate doesn't classify as 4xx
    // (tRPC puts the status at error.data.httpStatus, not error.status) — never retry.
    retry: false,
  });

  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (organization?.id) map.set(organization.id, `${organization.name} (You)`);
    // Prefer the owner-set nickname (client_alias) over the raw client org name.
    for (const r of (connectedClientsQuery.data ?? []) as ConnectedRow[]) {
      const id = r.client_organization_id;
      const name = r.client_alias ?? r.client_org?.name;
      if (id && name && !map.has(id)) map.set(id, name);
    }
    return map;
  }, [connectedClientsQuery.data, organization?.id, organization?.name]);

  const orgIdToNameRecord = useMemo(
    () => Object.fromEntries(orgIdToName),
    [orgIdToName],
  );

  const callerOrgId = organization?.id;

  const isCrossOrg = useCallback(
    (orgId: string | null | undefined) =>
      !!callerOrgId && !!orgId && orgId !== callerOrgId,
    [callerOrgId],
  );

  const labelFor = useCallback(
    (orgId: string | null | undefined) => {
      if (!orgId || orgId === callerOrgId) return undefined;
      return orgIdToName.get(orgId);
    },
    [callerOrgId, orgIdToName],
  );

  return {
    callerOrgId,
    orgIdToName,
    orgIdToNameRecord,
    isCrossOrg,
    labelFor,
  };
}
